import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import axios, { AxiosResponse } from 'axios'
import Layout from '@components/Layout'
import Banner from '@components/Banner'
import PlaneInCloudsSmall from '@images/planeInCloudsSmall.png'
import PlaneInCloudsMedium from '@images/planeInCloudsMedium.png'
import PlaneInCloudsBig from '@images/planeInCloudsBig.png'
import { colors } from '@styles/theme'
import { above } from '@styles/mixins/breakpoints'
import { SH2, STypographySmall } from '@components/Typography'
import Container from '@components/Container'
import HowItWorksOnAero from '@components/HowItWorksOnAero'
import Benefits from '@components/Benefits'
import { aeroCards as cards } from '@components/Benefits/data'
import HowToParticipate from '@components/HowToParticipate'
import WhereToApply from '@components/WhereToApply'
import Questions from '@components/Questions'
import LeavePopup from '@components/LeavePopup'
import CalcAero from '@components/CalcAero'
import Box from '@components/Box'
import { SLi, SList } from '@components/List'
import { api, ApiResponseStatuses } from '@components/LeavePopup/constants'
import { IVideoResponse, TypesKppLP } from '@components/VideoPlayer/types'
import VideoPlayer from '@components/VideoPlayer/VideoPlayer'
import { RequestApi } from '@/api/request'

const SBannerContainer = styled.div`
  background-color: ${colors.blueSky};
`

const SBanner = styled(Banner)`
  background-image: url(${PlaneInCloudsSmall});
  background-size: 320px;
  background-repeat: no-repeat;
  background-position: bottom;
  max-width: 1200px;

  & .bannerTitle {
    font-size: 22px;
    color: ${colors.graphite};
    width: 100%;
  }

  & .bannerText {
    color: ${colors.graphite};
    width: 100%;
  }

  & .bannerButtonAero:hover {
    border-color: ${colors.darkBlue};
    background-color: ${colors.darkBlue};
  }

  ${above('md')(css`
    background-image: url(${PlaneInCloudsMedium});
    background-size: 374px;
    background-position: right;

    & .bannerContentContainer {
      width: 438px;
    }

    & .bannerTitle {
      font-size: 32px;
    }

    & .bannerText {
      padding-right: 72px;
    }
  `)}

  ${above('xl')(css`
    background-image: url(${PlaneInCloudsBig});
    background-size: 670px;
    background-position: 526px center;
    padding: 38px 10px 38px 10px;
    font-size: 36px;
    line-height: 44px;

    & .bannerContentContainer {
      width: 517px;
      min-height: 280px;
    }

    & .bannerTitle {
      font-size: 36px;
      line-height: 44px;
    }

    & .bannerText {
      padding-right: 68px;
    }
  `)}
`

const SListContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${above('xl')(css`
    width: 482px;
  `)}
`

export const SLink = styled.a`
  color: ${colors.darkBlue};
`

export const STelLink = styled(STypographySmall)`
  display: inline;
  text-decoration: none;
`

const infoListData: JSX.Element[] = [
  <>
    Перейдите на официальный сайт СберНПФ по <SLink href="https://npfsberbanka.ru/aero/">внешней ссылке</SLink>
  </>,
  <>Нажмите кнопку «Оформить» и введите номер программы AF2072.</>,
  <>Рекомендуем воспользоваться входом через Сбербанк ID либо</>,
  <>Договор с АО «НПФ Сбербанка» поступит на ваш e-mail</>,
  <>Далее Вам необходимо передать оба заявления в бухгалтерию ПАО Аэрофлот.</>,
  <>После поступления первого взноса, удержанного из вашей заработной платы,ваш договор вступит в силу.</>,
]

const SInfoList = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0 0 40px;
  padding-left: 20px;
`

const SInfoListItem = styled.li`
  margin-bottom: 20px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const SIFrame = styled.iframe`
  width: 100%;
  height: 62vw;

  ${above('md')(css`
    height: 48vw;
  `)}

  ${above('xl')(css`
    width: 663px;
    height: 450px;
  `)}
`

const SAeroLi = styled(SLi)`
  &:before {
    background: ${colors.darkBlue};
  }
`

const popupIsActive = false

const Index: React.FC = () => {
  const [isPopupOpened, setIsPopupOpened] = useState(false)
  const [videoUrl, setVideoUrl] = useState('')

  const onOutside = () => {
    setIsPopupOpened(true)
  }

  useEffect(() => {
    if(popupIsActive) {
      if (localStorage.leavePopupWasShown !== 'true') {
        document.body.addEventListener('mouseleave', onOutside)
      }
  
      return () => document.body.removeEventListener('mouseleave', onOutside)
    }
  }, [])

  useEffect(() => {
    RequestApi.getVideoLink(TypesKppLP.aero)
      .then(
        ({
          data: {
            status,
            result: { link },
          },
        }) => {
          if (status === ApiResponseStatuses.success) {
            setVideoUrl(link)
          }
        },
      )
      .catch((err) => console.log(err))
  }, [])

  const onClose = useCallback(() => {
    setIsPopupOpened(false)
    localStorage.leavePopupWasShown = 'true'
    document.body.removeEventListener('mouseleave', onOutside)
  }, [])

  const benefitsRef = useRef<HTMLDivElement>(null)

  const onBannerButton = useCallback(
    () => benefitsRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    [benefitsRef],
  )

  return (
    <Layout
      lkHref="https://lk.npfsb.ru/?utm_source=sbernpfsite&utm_medium=header&utm_campaign=lkflpromo"
      contractHref="https://npo.npfsb.ru/?uniguid=AF2072&utm_source=sbernpfsite&utm_medium=lpdzo&utm_campaign=getkppcontract"
    >
      <SBannerContainer>
        <SBanner
          title="Корпоративная пенсионная программа для сотрудников Аэрофлот со СберНПФ"
          text="Корпоративная пенсионная программа позволяет каждому сотруднику накопить себе дополнительную пенсию вместе с Аэрофлот!"
          button={{
            onClick: onBannerButton,
            text: 'Почему это выгодно?',
          }}
        />
      </SBannerContainer>

      <Container mt="60px" display={{ _: 'block', xl: 'flex' }}>
        <Box width={{ _: '100%', xl: '45%' }} mr={{ _: '0', xl: '5%' }} mb={{ _: '40px', xl: '0' }}>
          <SH2 mb="24px">О программе за 2 минуты:</SH2>
          <SList>
            <SAeroLi>ПАО Аэрофлот ежемесячно софинансирует 20% от взноса сотрудника</SAeroLi>
            <SAeroLi>Сотрудник самостоятельно выбирает комфортный размер взноса</SAeroLi>
            <SAeroLi>Накопления не подлежат взысканию третьими лицами и не делятся при разводе</SAeroLi>
          </SList>
        </Box>
        <Box width={{ _: '100%', xl: '50%' }} display="flex" justifyContent="center">
          <VideoPlayer link={videoUrl} />
        </Box>
      </Container>

      <Container mt="80px" pr={{ _: 0, md: '40px' }}>
        <SH2 mb={{ _: '20px', xxl: '32px' }}>Как это работает?</SH2>
        <HowItWorksOnAero />
      </Container>

      <Box ref={benefitsRef}>
        <Container mt={{ _: '80px', xl: '86px' }}>
          <Benefits heading="Выгоды программы" cards={cards} color={colors.darkBlue} />
        </Container>
      </Box>

      <Container mt={{ _: '100px', md: '80px' }}>
        <SH2 mb={{ _: '20px', xl: '30px' }}>Рассчитайте вашу будущую пенсию</SH2>
        <CalcAero />
      </Container>

      <Container mt={{ _: '100px', md: '80px', xl: '120px' }} pr={{ _: '20px', md: 0, xl: '10px' }}>
        <HowToParticipate heading="Как стать участником программы" />
      </Container>

      <Container mt={{ _: '80px', md: '100px', xl: '80px' }}>
        <SH2>Где оформить?</SH2>
        <WhereToApply />
      </Container>

      <Container mt={{ _: '108px', md: '80px', xl: '84px' }}>
        <SH2 mb={{ _: '20px', md: '32px' }}>Вопросы и ответы</SH2>
        <Questions />
      </Container>

      {isPopupOpened && <LeavePopup onClose={onClose} />}
    </Layout>
  )
}

export default Index
